import type { Lang } from '../i18n/strings.ts';

/**
 * Код страны в поле номера при входе. Сербия первой, дальше соседи и страны, откуда
 * команда и покупатели: иностранный номер вводится без ручной правки кода.
 */
export type PhoneCountry = { iso: string; dialCode: string };

export const SERBIA: PhoneCountry = { iso: 'RS', dialCode: '+381' };

export const PHONE_COUNTRIES: readonly PhoneCountry[] = [
  SERBIA,
  { iso: 'ME', dialCode: '+382' },
  { iso: 'BA', dialCode: '+387' },
  { iso: 'HR', dialCode: '+385' },
  { iso: 'MK', dialCode: '+389' },
  { iso: 'SI', dialCode: '+386' },
  { iso: 'HU', dialCode: '+36' },
  { iso: 'RO', dialCode: '+40' },
  { iso: 'BG', dialCode: '+359' },
  { iso: 'GR', dialCode: '+30' },
  { iso: 'CY', dialCode: '+357' },
  { iso: 'TR', dialCode: '+90' },
  { iso: 'RU', dialCode: '+7' },
  { iso: 'BY', dialCode: '+375' },
  { iso: 'UA', dialCode: '+380' },
  { iso: 'KZ', dialCode: '+7' },
  { iso: 'GE', dialCode: '+995' },
  { iso: 'AM', dialCode: '+374' },
  { iso: 'IL', dialCode: '+972' },
  { iso: 'AE', dialCode: '+971' },
  { iso: 'DE', dialCode: '+49' },
  { iso: 'AT', dialCode: '+43' },
  { iso: 'CH', dialCode: '+41' },
  { iso: 'IT', dialCode: '+39' },
  { iso: 'FR', dialCode: '+33' },
  { iso: 'ES', dialCode: '+34' },
  { iso: 'PT', dialCode: '+351' },
  { iso: 'NL', dialCode: '+31' },
  { iso: 'BE', dialCode: '+32' },
  { iso: 'PL', dialCode: '+48' },
  { iso: 'CZ', dialCode: '+420' },
  { iso: 'SK', dialCode: '+421' },
  { iso: 'SE', dialCode: '+46' },
  { iso: 'NO', dialCode: '+47' },
  { iso: 'DK', dialCode: '+45' },
  { iso: 'FI', dialCode: '+358' },
  { iso: 'GB', dialCode: '+44' },
  { iso: 'IE', dialCode: '+353' },
  { iso: 'US', dialCode: '+1' },
  { iso: 'CA', dialCode: '+1' },
];

/** Флаг из региональных индикаторов по ISO-коду. */
export function countryFlag(country: PhoneCountry): string {
  return String.fromCodePoint(...[...country.iso].map((char) => 127_397 + (char.codePointAt(0) ?? 0)));
}

/** Название страны на языке интерфейса; сербский интерфейс латиницей. */
export function countryName(country: PhoneCountry, lang: Lang): string {
  try {
    return new Intl.DisplayNames([lang === 'sr' ? 'sr-Latn' : lang], { type: 'region' }).of(country.iso) ?? country.iso;
  } catch {
    return country.iso;
  }
}

export function onlyDigits(raw: string): string {
  return raw.replace(/\D/g, '');
}

/**
 * Номер целиком с кодом страны (автозаполнение, вставка «+357 96 144238» или
 * «0035796144238»): страна и остаток номера отдельно. Без кода страны — null.
 */
export function splitPhone(raw: string): { country: PhoneCountry; digits: string } | null {
  const digits = onlyDigits(raw);
  let withCode: string;
  if (raw.includes('+')) {
    withCode = digits;
  } else if (digits.startsWith('00')) {
    withCode = digits.slice(2);
  } else if (digits.length >= 11) {
    // Местный номер в списке стран не длиннее десяти цифр: одиннадцать и больше —
    // это номер с кодом страны без плюса (вставка «38160…»).
    withCode = digits;
  } else {
    return null;
  }

  // Из совпавших кодов берётся самый длинный; при равной длине (+7, +1) — первый в списке.
  let match: PhoneCountry | null = null;
  for (const country of PHONE_COUNTRIES) {
    if (!withCode.startsWith(country.dialCode.slice(1))) continue;
    if (!match || country.dialCode.length > match.dialCode.length) match = country;
  }
  return match ? { country: match, digits: withCode.slice(match.dialCode.length - 1) } : null;
}
